import { CurrencyDropdown } from "components";
import { WordTemplateCheckboxList } from "components/CheckboxList/WordTemplateCheckboxList";
import { BookkeepingLogoFileUpload } from "components/FileUpload/FileUploadWithValue";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import { CompanySmallDetails } from "components/SmallDetails";
import { BookkeepingCodeInput } from "components/ValidatedFields/BookkeepingCodeInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import moment from "moment";
import React, { useState } from "react";
import { BookkeepingInfo } from "./model";
import { UserCheckboxList } from "components/CheckboxList/UserCheckboxList";

interface BookkeepingDetailSectionProps {
  bookkeeping: server.dto.Bookkeeping
  isLastVisible: boolean
  isUsedInDocuments: boolean
  users: server.dto.UserSmall[]
  userIds: number[]
  wordTemplates: server.dto.KeyValueSublabel[]
  wordTemplateIds: number[]
  onCountryChanged: (countryKey: string) => void
}

export const BookkeepingDetailSection = (props: BookkeepingDetailSectionProps) => {
  const [bookkeeping, setBookkeeping] = useState<BookkeepingInfo>({
    visible: props.bookkeeping.visible,
    code: props.bookkeeping.code,
    title: props.bookkeeping.name,
    offset: props.bookkeeping.offset,
    resetOnNewYear: props.bookkeeping.resetOnNewYear,
    company: props.bookkeeping.company,
    companyAdmCountry: props.bookkeeping.companyAdmCountry,
    companyEuVatNumber: props.bookkeeping.companyEuVatNumber || "",
    baseCurrency: props.bookkeeping.baseCurrency,
    logo: props.bookkeeping.logo,
    contactDetails: props.bookkeeping.textForTemplate2 || "",
    userIds: props.userIds,
    wordTemplateIds: props.wordTemplateIds,
    displayCustomerNumberOnInvoice: props.bookkeeping.displayCustomerNumberOnInvoice,
    displayOwnerOnInvoice: props.bookkeeping.displayOwnerOnInvoice,
  });

  const canEditVisible = !props.isLastVisible || !bookkeeping.visible;
  const hideMonthOffsetRow = props.bookkeeping.monthOffset === 0;
  const hideOffset = bookkeeping.resetOnNewYear;

  const canEditCode = !props.isUsedInDocuments;
  const canEditResetOnNewYear = !props.isUsedInDocuments;
  const canEditOffset = !props.isUsedInDocuments;
  const canEditCompany = !props.isUsedInDocuments;
  const canEditBaseCurrency = !props.isUsedInDocuments;
  const canEditUsers = bookkeeping.visible;
  const canEditWordTemplates = bookkeeping.visible;

  const displayCurrency = serverReference.currencies.find((c) => c.value === bookkeeping.baseCurrency);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visible") {
      setBookkeeping({ ...bookkeeping, visible: value });
    } else if (key === "title") {
      setBookkeeping({ ...bookkeeping, title: value });
    } else if (key === "code") {
      setBookkeeping({ ...bookkeeping, code: value });
    } else if (key === "resetsOnNewYear") {
      if (value === true) {
        setBookkeeping({ ...bookkeeping, resetOnNewYear: true, offset: 0 });
      } else {
        setBookkeeping({ ...bookkeeping, resetOnNewYear: false });
      }
    } else if (key === "offset") {
      setBookkeeping({ ...bookkeeping, offset: value - 1 });
    } else if (key === "company") {
      setBookkeeping({ ...bookkeeping, company: value });
    } else if (key === "currency") {
      setBookkeeping({ ...bookkeeping, baseCurrency: value });
    } else if (key === "contactDetails") {
      setBookkeeping({ ...bookkeeping, contactDetails: value });
    } else if (key === "users") {
      setBookkeeping({ ...bookkeeping, userIds: value });
    } else if (key === "wordTemplates") {
      setBookkeeping({ ...bookkeeping, wordTemplateIds: value });
    } else if (key === "processedBy") {
      setBookkeeping({ ...bookkeeping, displayOwnerOnInvoice: value });
    } else if (key === "displayCustomerNumber") {
      setBookkeeping({ ...bookkeeping, displayCustomerNumberOnInvoice: value });
    } else {
      console.warn("Unknown bookkeeping key changed: " + key);
    }
  }

  const getUpdateBookkeepingInstruction = (bookkeepingInfo: BookkeepingInfo): server.dto.UpdateBookkeepingDetailsInstruction => ({
    baseCurrency: bookkeepingInfo.baseCurrency,
    bookkeepingId: props.bookkeeping.bookkeepingId,
    code: bookkeepingInfo.code,
    companyId: bookkeepingInfo.company.companyId,
    offset: bookkeepingInfo.offset,
    resetOnNewYear: bookkeepingInfo.resetOnNewYear,
    textForTemplate2: bookkeepingInfo.contactDetails,
    title: bookkeepingInfo.title,
    visible: bookkeepingInfo.visible,
    displayCustomerNumberOnInvoice: bookkeepingInfo.displayCustomerNumberOnInvoice,
    displayOwnerOnInvoice: bookkeepingInfo.displayOwnerOnInvoice,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      key: "bookkeeping",
      type: "regular",
      title: RESX.Bookkeeping.resxBookkeeping,
      fields: [{
        key: "visible",
        label: RESX.GeneralLabels.Visible,
        editDisabled: !canEditVisible,
        render: () => <YesNoText value={bookkeeping.visible} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.visible,
          submitChange: (visible: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            visible,
          }),
        }
      }, {
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{bookkeeping.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.bookkeeping.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.title,
          submitChange: (title: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            title,
          }),
        },
      }, {
        key: "code",
        label: RESX.Bookkeeping.Code,
        supportArticleId: serverReference.supportArticleIds.bookkeepingCode,
        editDisabled: !canEditCode,
        render: () => <div>{bookkeeping.code}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <BookkeepingCodeInput
            validatorKey="code"
            focusOnMount
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.code,
          submitChange: (code: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            code,
          }),
        },
      }, {
        key: "startsOnMonth",
        hidden: hideMonthOffsetRow,
        label: RESX.Bookkeeping.StartsOnMonth,
        render: () => <div>{moment.utc().month(props.bookkeeping.monthOffset).format("MMMM")}</div>,
      }, {
        key: "resetsOnNewYear",
        label: RESX.Bookkeeping.ResetOnNewYear,
        supportArticleId: serverReference.supportArticleIds.bookkeepingResetPerYear,
        editDisabled: !canEditResetOnNewYear,
        render: () => <YesNoText value={bookkeeping.resetOnNewYear} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.resetOnNewYear,
          submitChange: (resetOnNewYear: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            resetOnNewYear,
            offset: 0,
          }),
        },
      }, {
        key: "offset",
        label: RESX.Bookkeeping.StartingNumber,
        hidden: hideOffset,
        editDisabled: !canEditOffset,
        render: () => <div>{bookkeeping.offset + 1}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <RequiredNumberInput
            autoFocus
            width={90}
            validatorKey="amount"
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
            minValue={1}
            maxValue={serverReference.constants.bookkeeping.maxOffset}
          />,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.offset + 1,
          submitChange: (offset: number) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            offset: offset - 1,
          }),
        },
      }, {
        key: "currency",
        label: RESX.Financial.Currency,
        supportArticleId: serverReference.supportArticleIds.bookkeepingCurrency,
        editDisabled: !canEditBaseCurrency,
        render: () => <div>{displayCurrency.text} ({displayCurrency.name})</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Currencies>) => <CurrencyDropdown
            value={value}
            onChange={(currency) => {
              onChange(currency, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.baseCurrency,
          submitChange: (baseCurrency: server.dto.Currencies) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            baseCurrency,
          }),
        },
      }]
    }, {
      key: "detailsOnInvoice",
      type: "regular",
      title: RESX.Invoice.DetailsOnInvoice,
      fields: [{
        key: "logo",
        label: RESX.GeneralLabels.Logo,
        render: () => <BookkeepingLogoFileUpload
          bookkeepingId={props.bookkeeping.bookkeepingId}
          logo={bookkeeping.logo}
          onUploadComplete={(fileId: string, downloadUrl: string) => {
            setBookkeeping({ ...bookkeeping, logo: { fileId, downloadUrl } });
          }}
        />,
      }, {
        key: "company",
        label: RESX.Company.resxCompany,
        editDisabled: !canEditCompany,
        render: () => <CompanySmallDetails company={bookkeeping.company} layout="multiLine" />,
        inPageEdit: {
          render: ({ onChange }) => <div className="main-section-template-fields-padding">
            <CompanyModalPicker
              startOpen
              value={null}
              onChange={v => onChange(v, true)}
              modalTitle={RESX.Company.resxCompany}
              addressType="administrative"
              validatorKey="company"
              required={false}
              canCreateNew={false}
            />
          </div>,
          fullWidth: false,
          type: "custom",
          initialValue: bookkeeping.company,
          submitChange: async (c: server.dto.CompanySmall) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            companyId: c.companyId,
          }),
        },
      }, {
        key: "contactDetails",
        label: RESX.GeneralLabels.ContactDetails,
        isEmpty: !bookkeeping.contactDetails,
        render: () => <OptionalTextAreaDetails>{bookkeeping.contactDetails}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            placeholder={contactDetailsPlaceholder}
            maxLength={serverReference.constants.bookkeeping.maxTextForTemplateLength}
            value={value}
            onChange={(text) => { onChange(text); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.contactDetails,
          submitChange: (text: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            textForTemplate2: text,
          }),
        },
      }, {
        key: "displayConfigurationHeader",
        hideLabel: true,
        label: "",
        hidden: !site.currentEnvironment.invoice.hasV2,
        render: () => <h3>{RESX.GeneralLabels.Configuration}</h3>
      }, {
        key: "processedBy",
        label: RESX.InvoiceDocument.ProcessedBy,
        hidden: !site.currentEnvironment.invoice.hasV2,
        render: () => <YesNoText
          yesLabel={RESX.InvoiceDocument.YesDisplayInvoiceOwner}
          value={bookkeeping.displayOwnerOnInvoice}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.displayOwnerOnInvoice,
          submitChange: (displayOwnerOnInvoice: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            displayOwnerOnInvoice,
          })
        }
      }, {
        key: "displayCustomerNumber",
        label: RESX.Company.CustomerNumber,
        render: () => <YesNoText value={bookkeeping.displayCustomerNumberOnInvoice} />,
        hidden: !site.currentEnvironment.invoice.hasV2,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.displayCustomerNumberOnInvoice,
          submitChange: (displayCustomerNumberOnInvoice: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            displayCustomerNumberOnInvoice,
          })
        }
      }]
    }, {
      key: "users",
      type: "collapsable",
      title: RESX.SoUser.UsersThatCanAccess,
      fields: [{
        key: "users",
        label: RESX.SoUser.Users,
        hideLabel: true,
        editDisabled: !canEditUsers,
        render: () => <UserCheckboxList
          title={RESX.SoUser.Users}
          readonly
          canBeEmpty
          availableUsers={props.users}
          selectedUserIds={bookkeeping.userIds}
          onChanged={() => { }}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<number[]>) => <div className="main-section-template-fields-padding">
            <UserCheckboxList
              title={RESX.SoUser.Users}
              canBeEmpty
              availableUsers={props.users}
              selectedUserIds={value}
              onChanged={(selectedUserIds) => { onChange(selectedUserIds); }}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.userIds ?? [],
          submitChange: (userIds: number[]) => window.CoreApi.Bookkeeping.UpdateUserPermission({
            bookkeepingId: props.bookkeeping.bookkeepingId,
            userIds,
          })
        },
      }]
    }, {
      key: "wordTemplates",
      type: "collapsable",
      title: RESX.WordTemplate.resxWordTemplate,
      fields: [{
        key: "wordTemplates",
        label: RESX.WordTemplate.resxWordTemplate,
        hideLabel: true,
        editDisabled: !canEditWordTemplates,
        render: () => <WordTemplateCheckboxList
          title={RESX.GeneralLabels.Available}
          readonly
          canBeEmpty
          availableTemplates={props.wordTemplates}
          selectedTemplateIds={bookkeeping.wordTemplateIds}
          onChanged={() => { }}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<number[]>) => <div className="main-section-template-fields-padding">
            <WordTemplateCheckboxList
              title={RESX.GeneralLabels.Available}
              canBeEmpty
              availableTemplates={props.wordTemplates}
              selectedTemplateIds={value}
              onChanged={(selectedTemplateIds) => { onChange(selectedTemplateIds); }}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.wordTemplateIds ?? [],
          submitChange: (wordTemplateIds: number[]) => window.CoreApi.Bookkeeping.UpdateWordTemplateVisibility({
            bookkeepingId: props.bookkeeping.bookkeepingId,
            wordTemplateIds,
          }),
        }
      }]
    }]}
  />
}

const contactDetailsPlaceholder = `${RESX.GeneralLabels.ForInstance}: ${RESX.GeneralLabels.Email} | ${RESX.ContactDetails.PhoneNumber}`;